import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { LocationAutocompleteFormField } from 'src/shared/components/ui/forms/form-fields/location-autocomplete'
import type { PlaceResult } from 'src/shared/components/ui/forms/location-autocomplete'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useEventStore } from 'src/shared/store/event-store'

const propertyInfoSchema = z.object({
  propertyStreetAddress1: z.string().min(1, 'Street address is required'),
  propertyStreetAddress2: z.string().optional(),
  propertyCity: z.string().min(1, 'City is required'),
  propertyState: z.string().min(1, 'State is required'),
  propertyZip: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code'),
})

export const SpecificPropertyForm = () => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  const form = useForm<z.infer<typeof propertyInfoSchema>>({
    resolver: zodResolver(propertyInfoSchema),
    mode: 'onSubmit',
    defaultValues: {
      propertyStreetAddress1: (referralFormData.propertyStreetAddress1 as string) || '',
      propertyStreetAddress2: (referralFormData.propertyStreetAddress2 as string) || '',
      propertyCity: (referralFormData.propertyCity as string) || '',
      propertyState: (referralFormData.propertyState as string) || '',
      propertyZip: (referralFormData.propertyZip as string) || '',
    },
  })

  const handlePlaceSelected = (place: PlaceResult) => {
    form.setValue('propertyStreetAddress1', place.street)
    form.setValue('propertyCity', place.city)
    form.setValue('propertyState', place.state)
    form.setValue('propertyZip', place.zip)
  }

  const onSubmit = async (data: z.infer<typeof propertyInfoSchema>) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_specific_property_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-1">
        <LocationAutocompleteFormField
          control={form.control}
          name="propertyStreetAddress1"
          label="Street Address"
          handlePlaceSelected={handlePlaceSelected}
          locationTypes={['address']}
          locationRefValue="street"
        />
        <div className="flex flex-row items-center justify-between gap-2">
          <InputFormField control={form.control} name="propertyStreetAddress2" label="Apt, ste, etc" />
          <InputFormField control={form.control} name="propertyCity" label="City" />
        </div>
        <div className="flex flex-row items-center justify-between gap-2">
          <InputFormField control={form.control} name="propertyZip" label="Postal Code" />
          <InputFormField control={form.control} name="propertyState" label="State" />
        </div>

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
